import React, { useState } from "react";
import { Plus, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { IDENTITY_LABELS } from "@/lib/careerBuilder";
import { WsSection } from "@/components/negotiation/workspace/shared";
import api from "@/lib/api";
import { toast } from "sonner";
import { translateFr as tfr } from "@/i18n";

/* ---------- STEP 1 — Athlete identity (prefilled editable cards) ---------- */
function IdentityCard({ label, value, onChange, testid }) {
  const [editing, setEditing] = useState(false);
  const missing = !String(value || "").trim();
  return (
    <div className="rounded-xl border border-border bg-secondary/30 px-3.5 py-3 min-w-0" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      {editing ? (
        <Input
          autoFocus
          defaultValue={value || ""}
          className="h-8 mt-1 text-sm"
          onBlur={(e) => { onChange(e.target.value); setEditing(false); }}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          data-testid={`${testid}-input`}
        />
      ) : missing ? (
        <div className="flex items-center justify-between gap-2 mt-1">
          <Badge variant="outline" className="text-[9px] bg-danger/10 text-danger border-danger/30">Manquant</Badge>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1 text-athlynx hover:text-athlynx"
            onClick={() => setEditing(true)} data-testid={`${testid}-add`}>
            <Plus className="h-3 w-3" /> Ajouter
          </Button>
        </div>
      ) : (
        <button className="mt-1 text-sm font-medium truncate w-full text-left rounded hover:bg-secondary/60 transition-colors -mx-1 px-1"
          onClick={() => setEditing(true)} title={tfr("Modifier")} data-testid={`${testid}-value`}>
          {value}
        </button>
      )}
    </div>
  );
}

export function StepIdentity({ draft, update }) {
  const idn = draft.identity || {};
  return (
    <div data-testid="builder-step-identity">
      <WsSection title={tfr("Identité de l'athlète")}
        subtitle={tfr("Pré-rempli depuis le profil — cliquez sur une valeur pour la modifier.")}>
        <div className="flex items-center gap-3 mb-4 rounded-xl border border-border bg-secondary/30 px-3.5 py-3">
          <Avatar className="h-12 w-12">
            <AvatarImage src={idn.photo_url || undefined} alt="" />
            <AvatarFallback>{(idn.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate" data-testid="identity-photo-name">{idn.name || "—"}</p>
            <p className="text-xs text-muted-foreground truncate">{[idn.position, idn.club].filter(Boolean).join(" · ") || "Photo issue du profil"}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {IDENTITY_LABELS.map(([key, label]) => (
            <IdentityCard key={key} label={label} value={idn[key]}
              onChange={(v) => update(["identity", key], v)} testid={`identity-${key}`} />
          ))}
        </div>
      </WsSection>
    </div>
  );
}

/* ---------- STEP 2 — Career vision (templates + editable + AI) ---------- */
export function StepVision({ draft, update, catalogs, playerId }) {
  const v = draft.vision || {};
  const [ai, setAi] = useState(false);
  const applyTemplate = (tpl) => {
    update(["vision", "template_id"], tpl.id);
    update(["vision", "text"], tpl.text.replace("{h}", String(v.horizon_months || 36)));
  };
  const reformulate = async () => {
    if (!String(v.text || "").trim()) { toast.error(tfr("Écrivez d'abord une vision à reformuler")); return; }
    setAi(true);
    try {
      const r = await api.post(`/players/${playerId}/career-plan/builder-draft/vision/ai`, { text: v.text });
      update(["vision", "text"], r.data.text);
      toast.success(tfr("Vision reformulée par l'IA"));
    } catch (e) {
      if (e?.response?.status !== 402) toast.error(e?.response?.data?.detail || "Reformulation IA indisponible");
    } finally { setAi(false); }
  };
  return (
    <div data-testid="builder-step-vision">
      <WsSection title={tfr("Vision carrière")} subtitle={tfr("Choisissez un modèle — la phrase se crée automatiquement, puis reste modifiable.")}>
        <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label={tfr("Modèles de vision")}>
          {(catalogs?.vision_templates || []).map((tpl) => (
            <button key={tpl.id} type="button" onClick={() => applyTemplate(tpl)}
              aria-pressed={v.template_id === tpl.id}
              data-testid={`vision-tpl-${tpl.id}`}
              className={cn(
                "rounded-full border px-3 h-9 text-xs font-medium transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40",
                v.template_id === tpl.id ? "bg-athlynx text-white border-athlynx"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-athlynx/30"
              )}>
              {tpl.label}
            </button>
          ))}
        </div>
        <div className="rounded-xl border border-border bg-secondary/30 p-3.5">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Phrase de vision (modifiable)</p>
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={reformulate} disabled={ai}
              data-testid="vision-ai-btn">
              {ai ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-athlynx" />}
              {tfr("Reformuler avec l'IA")}
            </Button>
          </div>
          <Textarea value={v.text || ""} onChange={(e) => update(["vision", "text"], e.target.value)}
            rows={3} maxLength={400} className="text-sm resize-none" placeholder={tfr("Décrivez la vision de carrière…")}
            data-testid="vision-text" />
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <p className="text-xs text-muted-foreground shrink-0" id="vision-horizon-label">Horizon :</p>
            <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby="vision-horizon-label">
              {[12, 24, 36, 48, 60].map((h) => (
                <button key={h} type="button" onClick={() => update(["vision", "horizon_months"], h)}
                  data-testid={`vision-horizon-${h}`}
                  aria-pressed={v.horizon_months === h}
                  className={cn(
                    "h-9 min-w-[70px] px-3 rounded-full border text-xs font-medium tabular-nums inline-flex items-center justify-center transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40",
                    v.horizon_months === h
                      ? "bg-athlynx text-white border-athlynx"
                      : "bg-card dark:bg-transparent border-border text-muted-foreground hover:text-foreground hover:border-athlynx/30"
                  )}>
                  {h} mois
                </button>
              ))}
            </div>
          </div>
        </div>
      </WsSection>
    </div>
  );
}
